// Frontend/src/api/announcements.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "./client";
import {
  Announcement,
  AnnouncementListItem,
  CreateAnnouncementData,
  UpdateAnnouncementData,
} from "../types/announcement";

/**
 * List all announcements
 * - HOA Admins see announcements for their building
 * - Residents see only active announcements for their building
 * - Super Admins see all announcements
 */
export async function listAnnouncements(): Promise<AnnouncementListItem[]> {
  const res = await api.get<AnnouncementListItem[]>("/building/announcements/");
  return res.data;
}

/**
 * Get announcement details by ID
 */
export async function getAnnouncement(id: string): Promise<Announcement> {
  const res = await api.get<Announcement>(`/building/announcements/${id}/`);
  return res.data;
}

/**
 * Get only active announcements
 * Published and not expired
 */
export async function listActiveAnnouncements(): Promise<AnnouncementListItem[]> {
  const res = await api.get<AnnouncementListItem[]>(
    "/building/announcements/active/"
  );
  return res.data;
}

/**
 * Get only pinned announcements
 */
export async function listPinnedAnnouncements(): Promise<AnnouncementListItem[]> {
  const res = await api.get<AnnouncementListItem[]>(
    "/building/announcements/pinned/"
  );
  return res.data;
}

/**
 * Create a new announcement
 * Only HOA Admins and Super Admins can create
 */
export async function createAnnouncement(
  data: CreateAnnouncementData
): Promise<Announcement> {
  const res = await api.post<Announcement>("/building/announcements/", data);
  return res.data;
}

/**
 * Update an announcement
 * Only HOA Admins and Super Admins can update
 */
export async function updateAnnouncement(
  id: string,
  data: UpdateAnnouncementData
): Promise<Announcement> {
  const res = await api.put<Announcement>(`/building/announcements/${id}/`, data);
  return res.data;
}

/**
 * Delete an announcement
 * Only HOA Admins and Super Admins can delete
 */
export async function deleteAnnouncement(id: string): Promise<void> {
  await api.delete(`/building/announcements/${id}/`);
}

// ==================== React Query Hooks ====================

export const ANNOUNCEMENTS_QUERY_KEY = "announcements";

/**
 * Hook to fetch all announcements
 */
export function useAnnouncements() {
  return useQuery({
    queryKey: [ANNOUNCEMENTS_QUERY_KEY],
    queryFn: listAnnouncements,
  });
}

/**
 * Hook to fetch active announcements only
 */
export function useActiveAnnouncements() {
  return useQuery({
    queryKey: [ANNOUNCEMENTS_QUERY_KEY, "active"],
    queryFn: listActiveAnnouncements,
  });
}

/**
 * Hook to fetch pinned announcements only
 */
export function usePinnedAnnouncements() {
  return useQuery({
    queryKey: [ANNOUNCEMENTS_QUERY_KEY, "pinned"],
    queryFn: listPinnedAnnouncements,
  });
}

/**
 * Hook to fetch announcement details
 */
export function useAnnouncement(id: string) {
  return useQuery({
    queryKey: [ANNOUNCEMENTS_QUERY_KEY, id],
    queryFn: () => getAnnouncement(id),
    enabled: !!id,
  });
}

/**
 * Hook to create a new announcement
 */
export function useCreateAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAnnouncement,
    onSuccess: () => {
      // Invalidate all announcement queries to refetch
      queryClient.invalidateQueries({ queryKey: [ANNOUNCEMENTS_QUERY_KEY] });
    },
  });
}

/**
 * Hook to update an announcement
 */
export function useUpdateAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAnnouncementData }) =>
      updateAnnouncement(id, data),
    onSuccess: (_, variables) => {
      // Invalidate the specific announcement and the list
      queryClient.invalidateQueries({
        queryKey: [ANNOUNCEMENTS_QUERY_KEY, variables.id],
      });
      queryClient.invalidateQueries({ queryKey: [ANNOUNCEMENTS_QUERY_KEY] });
    },
  });
}

/**
 * Hook to delete an announcement
 */
export function useDeleteAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAnnouncement,
    onSuccess: () => {
      // Invalidate all announcement queries
      queryClient.invalidateQueries({ queryKey: [ANNOUNCEMENTS_QUERY_KEY] });
    },
  });
}
